import React, { useState } from "react";
import {
  Box,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  useMediaQuery,
  Collapse,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useClub } from "../../contexts/ClubContext";
import { useSeason } from "../../contexts/SeasonContext";
import { useRoster } from "../../contexts/RosterContext";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import PeopleIcon from "@mui/icons-material/People";
import SportsSoccerIcon from "@mui/icons-material/SportsSoccer";
import BarChartIcon from "@mui/icons-material/BarChart";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import GroupsIcon from "@mui/icons-material/Groups";
import SchoolIcon from "@mui/icons-material/School";
import SportsScoreIcon from "@mui/icons-material/SportsScore";
import SettingsIcon from "@mui/icons-material/Settings";

export default function TabletLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { logout } = useAuth();
  const { club } = useClub();
  const navigate = useNavigate();
  const isMobile = useMediaQuery((theme: any) => theme.breakpoints.down("sm"));
  const { selectedSeason, seasons, setSelectedSeason, createNextSeason } =
    useSeason();
  const { selectedRoster, rosters, setSelectedRoster } = useRoster();
  const CREATE_NEXT_SEASON = -1;
  const location = useLocation();

  const handleProfileClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleProfileClose();
    logout();
    navigate("/");
  };

  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleNavItemClick = (path: string) => {
    if (isMobile) {
      setDrawerOpen(false);
    }
    navigate(path);
  };

  // Handle roster selection change
  const handleRosterChange = (event: SelectChangeEvent<number>) => {
    const rosterId = event.target.value as number;
    const roster = rosters.find((r) => r.id === rosterId);
    if (roster) {
      setSelectedRoster(roster);
    }
  };

  // Handle season selection change
  const handleSeasonChange = (event: SelectChangeEvent<number>) => {
    const seasonId = event.target.value as number;

    if (seasonId === CREATE_NEXT_SEASON) {
      handleNextSeason();
      return;
    }

    const selectedSeason = seasons.find((s) => s.id === seasonId);
    if (selectedSeason) {
      setSelectedSeason(selectedSeason);
    }
  };

  // Handle next season creation
  const handleNextSeason = async () => {
    if (!seasons || !Array.isArray(seasons) || seasons.length === 0) {
      console.error("No seasons data available");
      return;
    }

    await createNextSeason({
      previous_season_name: selectedSeason?.name || "",
      previous_season_start_date: selectedSeason?.start_date || new Date(),
      previous_season_end_date: selectedSeason?.end_date || new Date(),
      new_season_name: `2025`,
      new_season_start_date: new Date(`2025-01-01`),
      new_season_end_date: new Date(`2025-12-31`),
    });
  };

  const drawerContent = (
    <>
      <div
        className="drawer-header"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          padding: "0 8px",
          minHeight: "64px",
        }}
      >
        <IconButton onClick={toggleDrawer}>
          {drawerOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
        </IconButton>
      </div>
      <Divider />
      <List>
        {/* Schedule Group */}
        <ListItem>
          <Typography variant="overline" color="text.secondary">
            Schedule
          </Typography>
        </ListItem>
        <ListItem
          button
          onClick={() => handleNavItemClick("/standings")}
          sx={{ 
            pl: 4,
            bgcolor: location.pathname === "/standings" ? "action.hover" : "transparent",
            "&:hover": {
              bgcolor: "action.hover"
            }
          }}
        >
          <ListItemIcon>
            <EmojiEventsIcon />
          </ListItemIcon>
          <ListItemText primary="Standings" />
        </ListItem>
        <ListItem
          button
          onClick={() => handleNavItemClick("/schedule")}
          sx={{ 
            pl: 4,
            bgcolor: location.pathname === "/schedule" ? "action.hover" : "transparent",
            "&:hover": {
              bgcolor: "action.hover"
            }
          }}
        >
          <ListItemIcon>
            <CalendarMonthIcon />
          </ListItemIcon>
          <ListItemText primary="Schedule" />
        </ListItem>

        <Divider sx={{ my: 0.5 }} />

        {/* Personnel Group */}
        <ListItem>
          <Typography variant="overline" color="text.secondary">
            Personnel
          </Typography>
        </ListItem>
        <ListItem
          button
          onClick={() => handleNavItemClick("/roster")}
          sx={{ 
            pl: 4,
            bgcolor: location.pathname === "/roster" ? "action.hover" : "transparent",
            "&:hover": {
              bgcolor: "action.hover"
            }
          }}
        >
          <ListItemIcon>
            <GroupsIcon />
          </ListItemIcon>
          <ListItemText primary="Roster" />
        </ListItem>
        <ListItem
          button
          onClick={() => handleNavItemClick("/recruits")}
          sx={{ 
            pl: 4,
            bgcolor: location.pathname === "/recruits" ? "action.hover" : "transparent",
            "&:hover": {
              bgcolor: "action.hover"
            }
          }}
        >
          <ListItemIcon>
            <SchoolIcon />
          </ListItemIcon>
          <ListItemText primary="Recruits" />
        </ListItem>
        <ListItem
          button
          onClick={() => handleNavItemClick("/depth-chart")}
          sx={{ 
            pl: 4,
            bgcolor: location.pathname === "/depth-chart" ? "action.hover" : "transparent",
            "&:hover": {
              bgcolor: "action.hover"
            }
          }}
        >
          <ListItemIcon>
            <AssignmentIndIcon />
          </ListItemIcon>
          <ListItemText primary="Depth Chart" />
        </ListItem>

        <Divider sx={{ my: 0.5 }} />

        {/* Schemes Group */}
        <ListItem>
          <Typography variant="overline" color="text.secondary">
            Schemes
          </Typography>
        </ListItem>
        <ListItem
          button
          onClick={() => handleNavItemClick("/playbook")}
          sx={{ 
            pl: 4,
            bgcolor: location.pathname === "/playbook" ? "action.hover" : "transparent",
            "&:hover": {
              bgcolor: "action.hover"
            }
          }}
        >
          <ListItemIcon>
            <MenuBookIcon />
          </ListItemIcon>
          <ListItemText primary="Playbook" />
        </ListItem>
        <ListItem
          button
          onClick={() => handleNavItemClick("/schemes/configure")}
          sx={{ 
            pl: 4,
            bgcolor: location.pathname === "/schemes/configure" ? "action.hover" : "transparent",
            "&:hover": {
              bgcolor: "action.hover"
            }
          }}
        >
          <ListItemIcon>
            <SettingsIcon />
          </ListItemIcon>
          <ListItemText primary="Configure" />
        </ListItem>

        <Divider sx={{ my: 0.5 }} />

        {/* Stats Group */}
        <ListItem>
          <Typography variant="overline" color="text.secondary">
            Stats
          </Typography>
        </ListItem>
        <ListItem
          button
          onClick={() => handleNavItemClick("/stats")}
          sx={{ 
            pl: 4,
            bgcolor: location.pathname === "/stats" ? "action.hover" : "transparent",
            "&:hover": {
              bgcolor: "action.hover"
            }
          }}
        >
          <ListItemIcon>
            <BarChartIcon />
          </ListItemIcon>
          <ListItemText primary="Home" />
        </ListItem>
        <ListItem
          button
          onClick={() => handleNavItemClick("/game-logger")}
          sx={{ 
            pl: 4,
            bgcolor: location.pathname === "/game-logger" ? "action.hover" : "transparent",
            "&:hover": {
              bgcolor: "action.hover"
            }
          }}
        >
          <ListItemIcon>
            <SportsScoreIcon />
          </ListItemIcon>
          <ListItemText primary="Game Logger" />
        </ListItem>
      </List>
    </>
  );

  const drawerWidth = 240;

  return (
    <Box sx={{ display: "flex", height: "100vh" }}>
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          width: "100%",
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="toggle drawer"
            edge="start"
            onClick={toggleDrawer}
            sx={{ mr: 2 }}
          >
            {drawerOpen ? <ChevronLeftIcon /> : <MenuIcon />}
          </IconButton>

          {/* Title and Logo together */}
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, justifyContent: 'center' }}>
            {club?.profile_picture && (
              <Box
                component="img"
                src={club.profile_picture}
                alt="Club Logo"
                sx={{
                  height: { xs: 32, sm: 40, md: 48 },
                  width: "auto",
                  marginRight: 6,
                  borderRadius: "4px",
                }}
              />
            )}
            <Typography
              variant="h1"
              component="div"
              sx={{
                fontSize: { xs: "1.5rem", sm: "2rem", md: "2.5rem" },
                color: "white",
                fontWeight: "bold",
                textShadow: "2px 2px 4px rgba(0,0,0,0.5)",
                letterSpacing: "0.05em",
                paddingY: 1,
              }}
            >
              {club?.name.replace("School", "")}
            </Typography>
          </Box>

          {/* Roster Selection */}
          <FormControl
            variant="outlined"
            size="small"
            sx={{
              minWidth: 150,
              mr: 2,
              "& .MuiOutlinedInput-root": {
                color: "white",
                "& fieldset": {
                  borderColor: "rgba(255, 255, 255, 0.23)",
                },
                "&:hover fieldset": {
                  borderColor: "rgba(255, 255, 255, 0.5)",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "white",
                },
              },
              "& .MuiInputLabel-root": {
                color: "rgba(255, 255, 255, 0.7)",
                "&.Mui-focused": {
                  color: "white",
                },
              },
            }}
          >
            <InputLabel id="roster-select-label">Roster</InputLabel>
            <Select
              labelId="roster-select-label"
              value={selectedRoster?.id || ""}
              onChange={handleRosterChange}
              label="Roster"
              disabled={rosters.length === 0}
            >
              {rosters.map((roster) => (
                <MenuItem key={roster.id} value={roster.id}>
                  {roster.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Season Selection */}
          <FormControl
            variant="outlined"
            size="small"
            sx={{
              minWidth: 150,
              mr: 2,
              "& .MuiOutlinedInput-root": {
                color: "white",
                "& fieldset": {
                  borderColor: "rgba(255, 255, 255, 0.23)",
                },
                "&:hover fieldset": {
                  borderColor: "rgba(255, 255, 255, 0.5)",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "white",
                },
              },
              "& .MuiInputLabel-root": {
                color: "rgba(255, 255, 255, 0.7)",
                "&.Mui-focused": {
                  color: "white",
                },
              },
            }}
          >
            <InputLabel id="season-select-label">Season</InputLabel>
            <Select
              labelId="season-select-label"
              value={selectedSeason?.id || ""}
              onChange={handleSeasonChange}
              label="Season"
            >
              {Array.isArray(seasons) &&
                seasons.map((season) => (
                  <MenuItem key={season.id} value={season.id}>
                    {season.name}
                  </MenuItem>
                ))}
              <MenuItem
                value={CREATE_NEXT_SEASON}
                sx={{
                  borderTop: "1px solid rgba(255, 255, 255, 0.12)",
                  mt: 1,
                  color: "primary.main",
                  fontWeight: "bold",
                }}
              >
                Create Next Season
              </MenuItem>
            </Select>
          </FormControl>

          <IconButton onClick={handleProfileClick} sx={{ color: "white" }}>
            <AccountCircleIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Responsive drawer */}
      <Drawer
        variant="persistent"
        open={drawerOpen}
        sx={{
          width: drawerOpen ? drawerWidth : 0,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            position: 'fixed',
            transition: (theme) =>
              theme.transitions.create(['width', 'margin'], {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Profile Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileClose}
      >
        <MenuItem onClick={handleLogout}>Logout</MenuItem>
      </Menu>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 1,
          width: '100%',
          height: 'calc(100vh - 64px)',
          marginTop: '64px',
          marginLeft: 1,
          transition: (theme) =>
            theme.transitions.create(['margin', 'width'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
          overflow: 'auto',
          position: 'relative',
          backgroundColor: 'background.default',
          color: 'text.primary',
        }}
      >
        {children}
      </Box>
    </Box>
  );
}